/**
 * FitText — shrinks its child text so it fits on a single line inside the
 * available width. Starts at `maxPx`, backs off in `stepPx` increments until
 * either the text no longer overflows OR `minPx` is reached.
 *
 * Why not use CSS `truncate`? The user asked for ADAPTIVE sizing — long
 * game names like "JOGO CÁLCULO MENTAL (GRANDE FINAL)" or long tier labels
 * like "3.º + 4.º ANO (Nível 1)" should be READABLE, not cut with ellipsis.
 *
 * Implementation is deliberately simple: one useLayoutEffect measures the
 * child on every text/size change and adjusts fontSize until it fits. This
 * runs off the DOM's own layout calculations so it stays cheap.
 */
import { useLayoutEffect, useRef, useState } from "react";

export default function FitText({
  children,
  maxPx = 24,
  minPx = 10,
  stepPx = 1,
  className = "",
  style = {},
  as: Tag = "div",
  "data-testid": testid,
  title,
}) {
  const ref = useRef(null);
  const [size, setSize] = useState(maxPx);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // Reset to max on every content change, then shrink to fit.
    let current = maxPx;
    el.style.fontSize = `${current}px`;
    // Reflow once so we get accurate scrollWidth.
    // Iterate until the line no longer overflows or we bottom out.
    let guard = 0;
    while (el.scrollWidth > el.clientWidth && current > minPx && guard < 200) {
      current -= stepPx;
      el.style.fontSize = `${current}px`;
      guard += 1;
    }
    setSize(current);

    // Re-fit on container resize (e.g., viewport change, sidebar collapse).
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => {
      let n = maxPx;
      el.style.fontSize = `${n}px`;
      let g = 0;
      while (el.scrollWidth > el.clientWidth && n > minPx && g < 200) {
        n -= stepPx;
        el.style.fontSize = `${n}px`;
        g += 1;
      }
      setSize(n);
    }) : null;
    if (ro) ro.observe(el);
    return () => { if (ro) ro.disconnect(); };
  }, [children, maxPx, minPx, stepPx]);

  return (
    <Tag
      ref={ref}
      data-testid={testid}
      title={title}
      className={`whitespace-nowrap overflow-hidden ${className}`}
      style={{ ...style, fontSize: `${size}px` }}
    >
      {children}
    </Tag>
  );
}
